import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, mergeProps as _mergeProps, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "mb-3 flex flex-wrap items-center gap-2.5" }
const _hoisted_3 = {
  key: 0,
  "data-test": "orders-summary",
  class: "text-[11.5px] text-gray-500"
}

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import BottlesList from '@/components/BottlesList.vue'
import NeuePagination from '@/components/neue/NeuePagination.vue'
import { paidOrdersSummary } from '@/composables/useCustomerLifetimeStats'
import { useCustomerRecord } from '@/composables/useCustomerRecord'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationships } from '@/store/mappers'

const PAGE_SIZE = 10

export default {
  __name: 'CustomerOrdersTab',
  setup(__props) {

const LISTS = ['orders', 'carts']

const INCLUDES = [
  'checkout',
  'cart',
  'cart.cart_items',
  'cart.cart_items.product',
  'store',
  'conversation_user_pair',
  'fulfillment_slot',
  'membership_tier',
  'distribution_list',
  'bottle_cycle',
  'bottle_payment'
]

const route = useRoute()
const { lifetimeState, lifetime } = useCustomerRecord()

const canReadOrders = computed(() => privilegeService.canAccessResourceAction('orders', null, 'read'))

const emptyRows = () => ({ orders: [], carts: [] })

const emptyListStatus = () => ({
  isLoading: false,
  isLoaded: false,
  isEmpty: false,
  hasError: false,
  totalRecords: 0,
  page: 0
})

const emptyStatus = () => ({ orders: emptyListStatus(), carts: emptyListStatus() })

const rows = ref(emptyRows())
const status = ref(emptyStatus())
const selectedList = ref(0)

const lists = computed(() => ({
  orders: mapRelationships(rows.value.orders),
  carts: mapRelationships(rows.value.carts)
}))

const pageRange = (page, total) => ({ from: 1 + page * PAGE_SIZE, to: Math.min((page + 1) * PAGE_SIZE, total) })

const summary = computed(() =>
  selectedList.value === 0 && lifetimeState.value === 'ready' ? paidOrdersSummary(lifetime.value) : ''
)

const toggleClass = selected => [
  'rounded-md px-3.5 py-1.5 text-[13px]',
  selected ? 'bg-gray-800 font-semibold text-white' : 'text-gray-700'
]

const listUrl = (id, kind, page) =>
  `conversation_user_pairs/${id}/${kind}?included=${INCLUDES.join(',')}&page_size=${PAGE_SIZE}&offset=${
    page * PAGE_SIZE
  }`

const loadList = kind => {
  const id = route.params.id
  const page = status.value[kind].page
  const isCurrent = () => id === route.params.id && page === status.value[kind].page
  status.value[kind].isLoading = true
  status.value[kind].hasError = false

  return api
    .call('GET', listUrl(id, kind, page))
    .then(r => {
      if (!isCurrent()) return
      rows.value[kind] = r.data.data
      Object.assign(status.value[kind], {
        totalRecords: r.data.total_records ?? r.data.data.length,
        isLoading: false,
        isLoaded: true
      })
    })
    .catch(() => {
      if (!isCurrent()) return
      Object.assign(status.value[kind], { isLoading: false, hasError: true })
    })
}

const handlePrev = (kind, ret) => {
  const list = status.value[kind]
  if (list.page > 0) {
    list.page--
    loadList(kind)
  }
  if (ret) ret()
}

const handleNext = (kind, ret) => {
  const list = status.value[kind]
  if ((list.page + 1) * PAGE_SIZE < list.totalRecords) {
    list.page++
    loadList(kind)
  }
  if (ret) ret()
}

const loadIfNeeded = kind => {
  const list = status.value[kind]
  if (!list.isLoaded && !list.isLoading) loadList(kind)
}

const onListChange = index => {
  selectedList.value = index
  loadIfNeeded(LISTS[index])
}

watch(
  () => route.params.id,
  () => {
    rows.value = emptyRows()
    status.value = emptyStatus()
    loadList(LISTS[selectedList.value])
  }
)

onMounted(() => loadList(LISTS[selectedList.value]))

return (_ctx, _cache) => {
  return (canReadOrders.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_unref(TabGroup), {
          "selected-index": selectedList.value,
          onChange: onListChange
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(TabList), { class: "inline-flex rounded-[9px] border border-gray-200 bg-gray-100 p-[3px]" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Tab), { as: "template" }, {
                    default: _withCtx(({ selected }) => [
                      _createElementVNode("button", {
                        type: "button",
                        "data-test": "orders-toggle",
                        class: _normalizeClass(toggleClass(selected))
                      }, "Orders", 2)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(Tab), { as: "template" }, {
                    default: _withCtx(({ selected }) => [
                      _createElementVNode("button", {
                        type: "button",
                        "data-test": "carts-toggle",
                        class: _normalizeClass(toggleClass(selected))
                      }, "Carts", 2)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              (summary.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(summary.value), 1))
                : _createCommentVNode("", true)
            ]),
            _createVNode(_unref(TabPanels), null, {
              default: _withCtx(() => [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(LISTS, (kind) => {
                  return _createVNode(_unref(TabPanel), { key: kind }, {
                    default: _withCtx(() => [
                      _createVNode(BottlesList, {
                        bottles: lists.value[kind],
                        tab: 'contacts',
                        "show-actions": true,
                        "is-loading": status.value[kind].isLoading,
                        "is-empty": status.value[kind].isEmpty,
                        "has-error": status.value[kind].hasError,
                        "page-size": PAGE_SIZE,
                        stacked: true
                      }, null, 8, ["bottles", "is-loading", "is-empty", "has-error"]),
                      (status.value[kind].totalRecords > PAGE_SIZE)
                        ? (_openBlock(), _createBlock(NeuePagination, _mergeProps({
                            key: 0,
                            size: status.value[kind].totalRecords
                          }, { ref_for: true }, pageRange(status.value[kind].page, status.value[kind].totalRecords), {
                            onPrev: ret => handlePrev(kind, ret),
                            onNext: ret => handleNext(kind, ret)
                          }), null, 16, ["size", "onPrev", "onNext"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 2
                  }, 1024)
                }), 64))
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["selected-index"])
      ]))
    : _createCommentVNode("", true)
}
}

}